// ═══════════════════════════════════════════════════════════════════════════
// FACTURATION — les tarifs VERSIONNÉS et le relevé lu dans l'audit (21/10/2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Demande du fondateur : « les tarifs ne doivent pas être hardcodés, on doit
// pouvoir les modifier à tout moment ». Ce client ne calcule RIEN : prix en
// vigueur, versions, montants du relevé viennent du serveur
// (`api/facturation.py`). Un prix absent est `null` — « non fixé » — jamais 0.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';

export interface Palier { a_partir_de: number; remise_pct: number }

export interface Tarif {
  id: string;
  unite: string;
  tenant_id: string | null;
  montant: string;
  devise: 'GNF' | 'EUR';
  paliers: Palier[];
  valable_du: string;
  valable_au: string | null;
  motif: string;
  cree_par: string;
  cree_le: string;
}

export interface Grille { a_la_date: string; tarifs: Tarif[]; programmes: Tarif[]; sans_prix: string[] }

export interface LigneReleve {
  tenant: string; cle: string | null; unite: string; quantite: number; devise: string | null; montant: string | null;
}

export interface Releve {
  mois: string;
  lignes: LigneReleve[];
  totaux: { tenant: string; devise: string; montant: string }[];
  unites_sans_prix: string[];
}

export interface NouveauTarif {
  unite: string;
  montant: string;
  devise: 'GNF' | 'EUR';
  tenant_id?: string | null;
  paliers: Palier[];
  valable_du?: string | null;
  motif: string;
}

export function useUnites() {
  return useQuery({
    queryKey: ['facturation', 'unites'],
    queryFn: async () => (await apiClient.get('/facturation/unites')).data.unites as string[],
    staleTime: 300_000,
  });
}

export function useGrille() {
  return useQuery({
    queryKey: ['facturation', 'tarifs'],
    queryFn: async () => (await apiClient.get('/facturation/tarifs')).data as Grille,
    staleTime: 15_000,
  });
}

export function useHistorique(unite: string | null, tenantId: string | null) {
  return useQuery({
    queryKey: ['facturation', 'historique', unite, tenantId],
    enabled: !!unite,
    queryFn: async () => (await apiClient.get('/facturation/tarifs/historique', {
      params: { unite, ...(tenantId ? { tenant_id: tenantId } : {}) },
    })).data.versions as Tarif[],
  });
}

export function useReleve(mois: string) {
  return useQuery({
    queryKey: ['facturation', 'releve', mois],
    queryFn: async () => (await apiClient.get('/facturation/consommation', { params: { mois } })).data as Releve,
    staleTime: 30_000,
  });
}

export function useNouveauTarif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (t: NouveauTarif) => (await apiClient.post('/facturation/tarifs', t)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['facturation'] }); },
  });
}
